/**
 * Copyright (c) 2024-2026 Creative Agentics Limited. All rights reserved.
 * Proprietary and confidential. Unauthorised use, reproduction or distribution
 * of this software, in whole or in part, is strictly prohibited.
 * See LICENSE.txt for full terms.
 */

// WHEN A SOURCE OF WEALTH INTERVIEW GOES OUT ON ITS OWN (v1_850).
//
// Until v1_850 the automatic actions sent an interview to every owner carrying
// sowInterviewRequired, a flag the model sets on its own judgement ("higher
// risk indicators") and that the document engine kept below High whenever the
// funds narrative said "personal" or "own funds". The firm's signed section 9
// was never read, and nothing checked the owners as a set (production
// 28/09/26: a Medium case, one person listed twice at 100 percent, a link to
// each spelling).
//
// Now an interview is sent automatically only where the firm's section 9 (or,
// with nothing signed, the platform standard) requires one for the case's tier,
// to each natural person owner at or above the case's ownership threshold with
// an email and no interview yet. Anywhere else the owner's row offers Send to
// the officer. And any of these on the owner list holds every automatic send
// and raises a data quality line: shares of natural person owners adding up to
// more than 100 percent, two owners sharing an email, two owner rows the name
// matcher calls one person. Pure.

import { samePerson as baseSamePerson, sameEntryPerson, closeNames } from './person-name-match.js';
import { personNames } from './sow-links.js';
import { holdingPercent, atOrAboveThreshold } from './ownership-threshold.js';
import { TIER_REQUIREMENTS } from './policy-questionnaire.js';

const TIERS = ['Low', 'Medium', 'High'];
// Mirrors the High case floor's corporate test (sow-floor.js), so the same
// people count as natural persons.
const CORP = /\b(ltd|limited|plc|llp|llc|inc|corp|corporation|gmbh|ag|sa|sarl|bv|nv|oy|ab|aps|pte|pty|holdings?|group|trust|foundation|nominees?)\b/i;
const isNatural = (u) => !!(u && u.name && u.corporateUBO !== true && u.corporateUBO !== 'true' && !(u.corporateUBO !== false && CORP.test(String(u.name))));
const normEmail = (e) => { const p = String(e || '').toLowerCase().trim().split('@'); return p.length === 2 && p[0] && p[1] ? p[0].split('+')[0] + '@' + p[1] : null; };

// What section 9 says for this tier, and on what basis.
export function sowRequiredForTier(tier, riskConfig) {
  const t = TIERS.includes(tier) ? tier : null;
  if (!t) return { required: false, basis: 'the case has no tier yet' };
  const rc = riskConfig && typeof riskConfig === 'object' ? riskConfig : {};
  const tr = rc.tierRequirements && rc.tierRequirements[t];
  const pb = rc.policyBasis && rc.policyBasis.version != null ? rc.policyBasis : null;
  if (tr && typeof tr.sowRequired === 'boolean') {
    return { required: tr.sowRequired, basis: pb ? `the firm's policy (section 9, policy questionnaire version ${pb.version})` : "the firm's configuration" };
  }
  return { required: !!TIER_REQUIREMENTS[t].sowRequired, basis: 'the platform standard (the firm has no signed section 9)' };
}

// The data quality lines on an owner list (empty when it is sound).
export function ownerDataQuality(ubos, match = baseSamePerson) {
  const people = (Array.isArray(ubos) ? ubos : []).filter(isNatural);
  const lines = [];
  const shares = people.map(u => holdingPercent(u.ownership)).filter(n => n !== null);
  const total = Math.round(shares.reduce((a, b) => a + b, 0) * 100) / 100;
  if (total > 100) lines.push(`The individual owners' shares add up to ${total} percent (${people.filter(u => holdingPercent(u.ownership) !== null).map(u => u.name + ' ' + holdingPercent(u.ownership) + ' percent').join(', ')}), more than 100 percent.`);
  const byEmail = {};
  for (const u of people) { const e = normEmail(u.email); if (e) (byEmail[e] = byEmail[e] || []).push(u.name); }
  // v1_852: close names on one address are one person listed twice (the line
  // below), not two people sharing an inbox.
  for (const [e, names] of Object.entries(byEmail)) {
    const reps = [];
    for (const nm of names) if (!reps.some(r => match(r, nm) || closeNames(r, nm))) reps.push(nm);
    if (reps.length > 1) lines.push(`${reps.join(' and ')} share one email address (${e}).`);
  }
  for (let i = 0; i < people.length; i++) for (let j = i + 1; j < people.length; j++) {
    if (sameEntryPerson(people[i], people[j], match)) lines.push(`${people[i].name} and ${people[j].name} are listed as two owners but read as one person.`);
  }
  return lines;
}

// v1_897: does this person already hold any interview link on the case?
export function autoSendAlreadyLinked(existing, u, match = baseSamePerson) {
  const names = personNames(u);
  const emails = [u && u.email, u && u.sowInterviewEmail].map(normEmail).filter(Boolean);
  return (existing || []).some(s => {
    if (!s) return false;
    if (s.contact_name && names.some(n => match(s.contact_name, n))) return true;
    const em = normEmail(s.contact_email);
    return !!em && emails.includes(em);
  });
}

// The plan for one run of the automatic actions.
//   existing: the case's interview sessions ({ contact_name, contact_email }).
export function sowAutoPlan({ ubos, tier, riskConfig, threshold = 25, existing = [], match = baseSamePerson }) {
  const req = sowRequiredForTier(tier, riskConfig);
  const dataQuality = ownerDataQuality(ubos, match);
  const held = [];
  const send = [];
  const candidates = (Array.isArray(ubos) ? ubos : []).filter(u => isNatural(u) && atOrAboveThreshold(u.ownership, threshold));
  for (const u of candidates) {
    // v1_897: one person never gets a second link automatically, whether the
    // first is open, complete or closed. Matched by any name form the case keeps
    // for them (v1_888) or the address on their row or the one an interview went
    // to. Diment 28/09/26: a link under "Mahsood Hussain Malik" was complete and
    // the row read "Mahsood H. Malik"; the send before v1_850 compared exact
    // names only and sent him a second interview.
    const invited = autoSendAlreadyLinked(existing, u, match);
    if (invited) { held.push({ name: u.name, reason: 'an interview already exists' }); continue; }
    if (dataQuality.length) { held.push({ name: u.name, reason: 'held: the owner list has a data quality problem' }); continue; }
    if (!req.required) { held.push({ name: u.name, reason: `not required at ${tier || 'this'} risk by ${req.basis}; the officer can send it from the case` }); continue; }
    if (!u.email) { held.push({ name: u.name, reason: 'no email on file' }); continue; }
    send.push({ name: u.name, email: u.email, ownership: u.ownership });
  }
  return { tier: tier || null, required: req.required, basis: req.basis, threshold, send, held, dataQuality, blocked: dataQuality.length > 0 };
}

// The case note for one automatic send: the real reason, never the model's flag.
export function sowAutoSendNote(p, u) {
  return `Source of wealth interview sent automatically to ${u.name} (${u.ownership || 'share not recorded'}) at ${u.email}: ${p.basis} requires a personal source of wealth interview at ${p.tier} risk for each individual owner at or above ${p.threshold} percent.`;
}

// The case note and the panel line when the owner list holds the sends.
export function sowAutoHoldNote(lines) {
  if (!lines || !lines.length) return null;
  return `Automatic source of wealth invitations are held until the owner list is corrected: ${lines.join(' ')} Check the owners, then send each interview from the owner's row.`;
}
